// Presentation taxonomy for the curated capability set — brand logo key,
// category (for the grouped picker), and a terse essence shown on cards. This is
// PRESENTATION metadata, deliberately client-side: the backend catalog stays the
// source of truth for what exists; this just decides how the known curated set
// is grouped + labelled (mirrors the v09 playground). A capability not listed
// here degrades gracefully — no category (falls into "Other"), a neutral glyph
// logo, and its server `summary` as the subtitle. If a new curated MCP/skill
// ships in seed.ts, add a line here to give it a logo + home.

export type CapBrand =
	| 'amazon'
	| 'asana'
	| 'otto'
	| 'kaufland'
	| 'allegro'
	| 'jtl'
	| 'plytix'
	| 'krea'
	// Official brand marks added for the newer marketplace MCPs.
	| 'shopify'
	| 'zendesk'
	| 'confluence'
	| 'linear'
	| 'posthog'
	| 'typeform'
	| 'google'
	| 'slack'
	| 'miro'
	| 'figma'
	| 'bidx'
	| 'home24'
	| 'xydc'
	| 'server'
	// No clean Simple Icon → rendered as a tasteful lucide-style glyph.
	| 'klaviyo'
	| 'fireflies'
	// Neutral lucide-style glyphs (data/skill/personal caps).
	| 'database'
	| 'chart'
	| 'pulse'
	| 'sparkle'
	| 'list'
	| 'globe'
	| 'cart'
	| 'message'
	| 'shapes';

export type CapPresentation = {
	brand: CapBrand;
	category?: string; // mcp only; skills are shown ungrouped
	essence: string; // <= ~4 words, the calm card subtitle
	// Merged-card display name (A1). Both twins of a read/write pair share ONE
	// label so buildCards() renders a single clean card name (e.g. "Asana")
	// regardless of which twin is the active entry. Singletons carry their own.
	label: string;
};

// Category order for the grouped page + picker. Skills carry an explicit
// category so they group cleanly (anything still uncategorized — e.g. personal
// caps — falls into a trailing "Other").
export const CATEGORY_ORDER = ['Amazon', 'Catalog & Listings', 'Data & Analytics', 'Work & Creative', 'Skills'];

// NOTE: most read/write twins share label + essence. AMC is also one product in
// the marketplace even though Analytics and admin-only Inventory remain separate,
// independently attested internal MCP surfaces.
export const CAP_PRESENTATION: Record<string, CapPresentation> = {
	'amazon-ads': { brand: 'amazon', category: 'Amazon', essence: 'Ad campaigns & performance', label: 'Amazon Ads' },
	'amazon-amc': { brand: 'amazon', category: 'Amazon', essence: 'Existing-object inventory', label: 'Amazon Marketing Cloud Admin Inventory' },
	'amazon-amc-readonly': { brand: 'amazon', category: 'Amazon', essence: 'Attribution & customer journeys', label: 'Amazon Marketing Cloud Analytics' },
	'amazon-seller': { brand: 'amazon', category: 'Amazon', essence: 'Listings, orders & reports', label: 'Amazon Seller' },
	plytix: { brand: 'plytix', category: 'Catalog & Listings', essence: 'Product information', label: 'Plytix' },
	otto: { brand: 'otto', category: 'Catalog & Listings', essence: 'OTTO marketplace', label: 'Otto' },
	kaufland: { brand: 'kaufland', category: 'Catalog & Listings', essence: 'Kaufland marketplace', label: 'Kaufland' },
	allegro: { brand: 'allegro', category: 'Catalog & Listings', essence: 'Allegro marketplace (Poland)', label: 'Allegro' },
	'jtl-erp': { brand: 'jtl', category: 'Catalog & Listings', essence: 'Orders, customers & stock', label: 'JTL' },
	'erp-data': { brand: 'database', category: 'Data & Analytics', essence: 'Sales, stock & margins', label: 'Sayano ERP' },
	'erp-data-readonly': { brand: 'database', category: 'Data & Analytics', essence: 'Sales, stock & margins', label: 'Sayano ERP' },
	analytics: { brand: 'chart', category: 'Data & Analytics', essence: 'All your data — sales, ads, traffic & more', label: 'Sayano Data Warehouse' },
	asana: { brand: 'asana', category: 'Work & Creative', essence: 'Projects & tasks', label: 'Asana' },
	monitoring: { brand: 'pulse', category: 'Work & Creative', essence: 'Uptime monitors', label: 'Monitoring' },
	krea: { brand: 'krea', category: 'Work & Creative', essence: 'AI image & video', label: 'Krea' },
	// Read-only twins — same brand + category + label + PLAIN essence as the write
	// parent (buildCards merges the pair into one card).
	'amazon-ads-readonly': { brand: 'amazon', category: 'Amazon', essence: 'Ad campaigns & performance', label: 'Amazon Ads' },
	'amazon-seller-readonly': { brand: 'amazon', category: 'Amazon', essence: 'Listings, orders & reports', label: 'Amazon Seller' },
	'plytix-readonly': { brand: 'plytix', category: 'Catalog & Listings', essence: 'Product information', label: 'Plytix' },
	'otto-readonly': { brand: 'otto', category: 'Catalog & Listings', essence: 'OTTO marketplace', label: 'Otto' },
	'kaufland-readonly': { brand: 'kaufland', category: 'Catalog & Listings', essence: 'Kaufland marketplace', label: 'Kaufland' },
	'asana-readonly': { brand: 'asana', category: 'Work & Creative', essence: 'Projects & tasks', label: 'Asana' },
	'monitoring-readonly': { brand: 'pulse', category: 'Work & Creative', essence: 'Uptime monitors', label: 'Monitoring' },
	// Newer MCPs (give each a home + logo so they leave the "More"/Other bucket).
	crawl4ai: { brand: 'globe', category: 'Work & Creative', essence: 'Web crawling', label: 'Web Crawler' },
	posthog: { brand: 'posthog', category: 'Data & Analytics', essence: 'Product analytics', label: 'PostHog' },
	xydc: { brand: 'xydc', category: 'Amazon', essence: 'Competitor & keyword intelligence', label: 'Xiyou Insights' },
	'google-ads': { brand: 'google', category: 'Data & Analytics', essence: 'Ad campaigns & performance', label: 'Google Ads' },
	'google-ads-readonly': { brand: 'google', category: 'Data & Analytics', essence: 'Ad campaigns & performance', label: 'Google Ads' },
	'google-analytics': { brand: 'google', category: 'Data & Analytics', essence: 'Web traffic & conversions', label: 'Google Analytics' },
	'google-analytics-readonly': { brand: 'google', category: 'Data & Analytics', essence: 'Web traffic & conversions', label: 'Google Analytics' },
	gdrive: { brand: 'google', category: 'Work & Creative', essence: 'Files & folders', label: 'Google Drive' },
	'gdrive-readonly': { brand: 'google', category: 'Work & Creative', essence: 'Files & folders', label: 'Google Drive' },
	'google-sheets': { brand: 'google', category: 'Work & Creative', essence: 'Spreadsheets', label: 'Google Sheets' },
	'google-sheets-readonly': { brand: 'google', category: 'Work & Creative', essence: 'Spreadsheets', label: 'Google Sheets' },
	'google-slides': { brand: 'google', category: 'Work & Creative', essence: 'Presentations', label: 'Google Slides' },
	'google-slides-readonly': { brand: 'google', category: 'Work & Creative', essence: 'Presentations', label: 'Google Slides' },
	'google-docs': { brand: 'google', category: 'Work & Creative', essence: 'Documents', label: 'Google Docs' },
	'google-docs-readonly': { brand: 'google', category: 'Work & Creative', essence: 'Documents', label: 'Google Docs' },
	'google-calendar': { brand: 'google', category: 'Work & Creative', essence: 'Events & scheduling', label: 'Google Calendar' },
	'google-calendar-readonly': { brand: 'google', category: 'Work & Creative', essence: 'Events & scheduling', label: 'Google Calendar' },
	gmail: { brand: 'google', category: 'Work & Creative', essence: 'Email', label: 'Gmail' },
	'gmail-readonly': { brand: 'google', category: 'Work & Creative', essence: 'Email', label: 'Gmail' },
	fireflies: { brand: 'fireflies', category: 'Work & Creative', essence: 'Meeting notes', label: 'Fireflies' },
	miro: { brand: 'miro', category: 'Work & Creative', essence: 'Visual collaboration', label: 'Miro' },
	figma: { brand: 'figma', category: 'Work & Creative', essence: 'Design file context', label: 'Figma' },
	bidx: { brand: 'bidx', category: 'Data & Analytics', essence: 'Marketplace advertising', label: 'BidX' },
	home24: { brand: 'home24', category: 'Catalog & Listings', essence: 'Home24 marketplace', label: 'Home24' },
	'shopify-admin': { brand: 'shopify', category: 'Catalog & Listings', essence: 'Shopify store', label: 'Shopify Admin' },
	// Ported Companion skills → the "Skills" section. Skills carry a NEUTRAL glyph
	// brand (never a branded mark like otto/krea/asana): in the redesign skills are
	// rendered with a monochrome glyph chip so a skill can never be mistaken for a
	// branded tool. Pick a topical lucide glyph per skill.
	'market-research-swot': { brand: 'chart', category: 'Skills', essence: 'Survey → SWOT', label: 'Market Research' },
	'excalidraw-diagrams': { brand: 'shapes', category: 'Skills', essence: 'Inline diagrams & sketches', label: 'Excalidraw Diagrams' },
	'vacation-handover': { brand: 'list', category: 'Skills', essence: 'Launch handover', label: 'Vacation Handover' },
	'otto-gap-analysis': { brand: 'chart', category: 'Skills', essence: 'Otto listing gaps', label: 'Otto Gap Analysis' },
	'list-on-otto': { brand: 'cart', category: 'Skills', essence: 'List products on Otto (single or bulk)', label: 'List on Otto' },
	'kaufland-gap-analysis': { brand: 'chart', category: 'Skills', essence: 'Kaufland listing gaps', label: 'Kaufland Gap Analysis' },
	'list-on-kaufland': { brand: 'cart', category: 'Skills', essence: 'List products on Kaufland (single or bulk)', label: 'List on Kaufland' },
	'krea-image': { brand: 'sparkle', category: 'Skills', essence: 'AI images — pick your model', label: 'Krea Image' },
	'xydc-insights': { brand: 'chart', category: 'Skills', essence: 'Competitor & keyword research', label: 'Xiyou Research' },
	// Read-only ops + per-user issue tracking.
	'coolify-readonly': { brand: 'pulse', category: 'Work & Creative', essence: 'Infra inspection', label: 'Coolify' },
	linear: { brand: 'linear', category: 'Work & Creative', essence: 'Issues & projects', label: 'Linear' },
	slack: { brand: 'slack', category: 'Work & Creative', essence: 'Messages & search', label: 'Slack' },
	typeform: { brand: 'typeform', category: 'Data & Analytics', essence: 'Forms & responses', label: 'Typeform' },
	zendesk: { brand: 'zendesk', category: 'Work & Creative', essence: 'Support tickets', label: 'Zendesk' },
	klaviyo: { brand: 'klaviyo', category: 'Data & Analytics', essence: 'Email & SMS marketing', label: 'Klaviyo' },
	confluence: { brand: 'confluence', category: 'Work & Creative', essence: 'Company wiki', label: 'Confluence' },
	ssh: { brand: 'pulse', category: 'Work & Creative', essence: 'Server access (restricted)', label: 'SSH' },
	// Read/write twin readonly halves — mirror the write parent (buildCards merges the pair).
	'shopify-admin-readonly': { brand: 'shopify', category: 'Catalog & Listings', essence: 'Shopify store', label: 'Shopify Admin' },
	'zendesk-readonly': { brand: 'zendesk', category: 'Work & Creative', essence: 'Support tickets', label: 'Zendesk' },
	'klaviyo-readonly': { brand: 'klaviyo', category: 'Data & Analytics', essence: 'Email & SMS marketing', label: 'Klaviyo' },
	'confluence-readonly': { brand: 'confluence', category: 'Work & Creative', essence: 'Company wiki', label: 'Confluence' },
};


const LEGACY_AMC_ADMIN_PRESENTATION: CapPresentation = {
	brand: 'amazon',
	category: 'Amazon',
	essence: 'Analysis-only deep AMC evidence',
	label: 'Amazon Marketing Cloud (AMC)',
};

export function presentation(id: string, latestVersion?: string): CapPresentation | undefined {
	if (id === 'amazon-amc' && latestVersion === '1.0.0') {
		return LEGACY_AMC_ADMIN_PRESENTATION;
	}
	return CAP_PRESENTATION[id];
}

const PERSONAL_MCP_BRANDS = new Set<CapBrand>([
	'amazon',
	'asana',
	'otto',
	'kaufland',
	'allegro',
	'jtl',
	'plytix',
	'krea',
	'shopify',
	'zendesk',
	'confluence',
	'linear',
	'posthog',
	'typeform',
	'google',
	'slack',
	'klaviyo',
	'fireflies',
	'miro',
	'bidx',
	'home24',
	'xydc',
]);

function isPersonalMcpBrand(value: string): value is CapBrand {
	return PERSONAL_MCP_BRANDS.has(value as CapBrand);
}

// Personal MCP ids are user-scoped and therefore absent from the curated table.
// Prefer a recognized persisted key, but also infer the common services for
// existing rows created before logo keys were stored. Unknown keys fail closed
// to the neutral server mark. `latestVersion` is forwarded so AMC v1/v2 labels
// retain their rollout-specific meaning.
export function presentationForCapability(
	id: string,
	name?: string,
	icon?: string | null,
	latestVersion?: string,
): CapPresentation {
	const known = presentation(id, latestVersion);
	if (known) return known;

	const haystack = `${id} ${name ?? ''}`.toLowerCase();
	const inferred = haystack.includes('miro')
		? 'miro'
		: haystack.includes('fireflies')
			? 'fireflies'
			: haystack.includes('klaviyo')
				? 'klaviyo'
				: haystack.includes('kaufland')
					? 'kaufland'
				: haystack.includes('allegro')
					? 'allegro'
					: haystack.includes('amazon-amc') ||
						  haystack.includes('amazon marketing cloud') ||
						  /(^|[^a-z0-9])amc([^a-z0-9]|$)/.test(haystack)
						? 'amazon'
						: haystack.includes('bidx')
							? 'bidx'
							: haystack.includes('home24')
								? 'home24'
								: haystack.includes('xydc') || haystack.includes('xiyou')
									? 'xydc'
									: undefined;
	const persisted = icon?.trim().toLowerCase() ?? '';
	const brand = inferred ?? (isPersonalMcpBrand(persisted) ? persisted : 'server');
	return { brand, label: name ?? id, essence: name ?? id };
}

// Group entries by category in CATEGORY_ORDER; anything uncategorized (personal
// caps, or a new curated cap not yet in the map) lands in a trailing "Other".
export function groupByCategory<T extends { id: string }>(
	entries: T[],
): { category: string; items: T[] }[] {
	const buckets = new Map<string, T[]>();
	for (const e of entries) {
		const cat = CAP_PRESENTATION[e.id]?.category ?? 'Other';
		const arr = buckets.get(cat);
		if (arr) arr.push(e);
		else buckets.set(cat, [e]);
	}
	const ordered: { category: string; items: T[] }[] = [];
	for (const cat of CATEGORY_ORDER) {
		const items = buckets.get(cat);
		if (items?.length) ordered.push({ category: cat, items });
	}
	const other = buckets.get('Other');
	if (other?.length) ordered.push({ category: 'Other', items: other });
	return ordered;
}
